import React, { memo, ReactElement, useCallback, useEffect, useState } from 'react'
import ReactSelect from 'react-select'
import { FieldEventTypes } from '../../Form'

type OptionTypes = {
  value: string
  label: string
}

type InputWithPrefixPropTypes = {
  id: string
  name: string
  type?: 'text' | 'email' | 'password'
  placeholder: string
  error?: string
  half?: boolean
  prefixList: OptionTypes[]
  defaultValue?: string
  prefixLarge?: boolean
  onChange?(event: FieldEventTypes): void
  onBlur?(event: FieldEventTypes): void
  onInput?(event: FieldEventTypes): void
}

const InputWithPrefix = ({
  id,
  name,
  type = 'text',
  placeholder,
  error,
  half,
  prefixList,
  defaultValue,
  prefixLarge,
  onChange,
  onBlur,
  onInput,
}: InputWithPrefixPropTypes): ReactElement => {
  const [prefixValue, setPrefixValue] = useState<string>(defaultValue || '')
  const [value, setValue] = useState<string>(defaultValue || '')
  const [fullValue, setFullValue] = useState<string>('')
  const hasOnlyOneOption = prefixList.length === 1

  useEffect(() => {
    if (hasOnlyOneOption) setPrefixValue(prefixList[0].value)
  }, [])

  const handleFullValue = useCallback(
    (prefix, currValue) => {
      let newValue = ''

      if (prefix && currValue) {
        newValue = `${prefix} ${currValue}`
      }

      setFullValue(newValue)
      if (onChange) {
        onChange({
          currentTarget: {
            name,
            value: newValue,
          },
        })
      }
    },
    [name, onChange],
  )

  const handleFullBlur = useCallback(
    (prefix, currValue) => {
      let newValue = ''

      if (prefix && currValue) {
        newValue = `${prefix} ${currValue}`
      }

      if (onBlur) {
        onBlur({
          currentTarget: {
            name,
            value: newValue,
          },
        })
      }
    },
    [name, onBlur],
  )

  const handlePrefixChange = useCallback(
    (selectedPrefix) => {
      setPrefixValue(selectedPrefix.value)
      handleFullValue(selectedPrefix.value, value)
      handleFullBlur(selectedPrefix.value, value)
    },
    [handleFullBlur, handleFullValue, value],
  )

  const handleChange = useCallback(
    (e) => {
      setValue(e.target.value)
      handleFullValue(prefixValue, e.target.value)
    },
    [handleFullValue, prefixValue],
  )

  const handleBlur = useCallback(
    (e) => {
      setValue(e.target.value)
      handleFullBlur(prefixValue, e.target.value)
    },
    [handleFullBlur, prefixValue],
  )

  return (
    <div className={`form-field-container${half ? ' half' : ''}`}>
      <div className={`form-field${error ? ' has-error' : ''}`}>
        <div className="form-field-input-container">
          <input type="hidden" name={name} value={fullValue} />
          <ReactSelect
            options={prefixList}
            onChange={handlePrefixChange}
            className={`form-field-input-prefix${prefixLarge ? ' large' : ''}`}
            placeholder="Prefix"
            defaultValue={hasOnlyOneOption ? prefixList[0] : null}
          />
          <input
            type={type}
            id={id}
            placeholder={placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            onInput={onInput}
            value={value}
          />
        </div>
        <label htmlFor={id}>{placeholder}</label>
      </div>
      <p className="form-field-error">{error}</p>
    </div>
  )
}

export default memo(InputWithPrefix)
